import { useCallback, useMemo, useRef, useState } from 'react';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { Box, ClickAwayListener } from '@mui/material';
import type { ValueViewerProps } from 'common/types/scalarSpreadsheet';
import FastValueViewer from 'components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useLayoutContext } from 'context/LayoutContext';
import CrossReferencePopupSheet from './CrossReferencePopupSheet';
import {
  CrossRefIconButton,
  CrossRefPopper,
  ICON_COLOR_MAP,
  OriginalViewerWrapper,
  ViewerContainer,
} from './CrossReferenceValueViewer.styles';
import useCrossReferenceMatch from './useCrossReferenceMatch';
import { extractFormatFromCell } from './utils/extractFormatFromCell';

// Host numeric cells can arrive here as JS `number` OR as a numeric string
// (e.g. cap-table totals computed by `totalParser` end up string-typed by the
// time the value-viewer renders). Coerce numeric strings to `number`; treat
// blank / non-numeric values as "no host value" so the icon stays gray.
const toHostValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

const CrossReferenceValueViewer: React.FC<ValueViewerProps> = ({ cell, ...rest }) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const config = cell.crossReference;
  const cellKey = cell.crossReferenceCellKey ?? '';
  const numericHostValue = toHostValue(cell.value);

  const color = useCrossReferenceMatch(cellKey, numericHostValue);

  const { isExportMode } = useLayoutContext();

  const OriginalViewer = cell.originalValueViewer ?? FastValueViewer;

  const toggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  // Inherited format is stable for a given cell (number/decimal-format-wise)
  // but cell.value changes per render. Memoize on the format-shaping fields
  // only so the popup doesn't see a new prop identity on every host re-render.
  const inheritedFormat = useMemo(
    () =>
      extractFormatFromCell({
        gridType: cell.gridType,
        format: cell.format,
        currency: cell.currency,
        dbDecimalPlaces: cell.dbDecimalPlaces,
        maxDecimalDigits: cell.maxDecimalDigits,
      }),
    [cell.gridType, cell.format, cell.currency, cell.dbDecimalPlaces, cell.maxDecimalDigits]
  );

  // If the cell hasn't opted in (or is missing its cellKey), behave exactly
  // like the wrapped original. Without a cellKey we can't address the store.
  // In PDF export mode, also fall back to the original viewer so the
  // cross-reference icon isn't captured in exported pages.
  if (!config || !cellKey || isExportMode) {
    return <OriginalViewer cell={cell} {...rest} />;
  }

  const iconFill = ICON_COLOR_MAP[color];

  return (
    <ViewerContainer ref={anchorRef} onKeyDown={handleKeyDown}>
      <OriginalViewerWrapper>
        <OriginalViewer cell={cell} {...rest} />
      </OriginalViewerWrapper>
      <CrossRefIconButton
        size="small"
        iconColor={iconFill}
        onMouseDown={event => event.stopPropagation()}
        onClick={toggle}
        aria-label="Open cross-reference"
        data-testid={`cross-ref-toggle-${cellKey}`}>
        {color === 'red' ? (
          <AssignmentLateIcon fontSize="small" style={{ fill: iconFill }} />
        ) : (
          <AssignmentTurnedInIcon fontSize="small" style={{ fill: iconFill }} />
        )}
      </CrossRefIconButton>
      {/*
        Close on outside click. With the popup decoupled from the host
        workbook (no cross-sheet '=' references), there's no longer a
        reason to keep it open while the user clicks elsewhere. Clicks on
        the toggle icon and Escape still work as additional dismiss paths.

        Lazy-mount the popup so closed cells don't run a useWorkbook each
        render. Mounting on toggle gives the popup a fresh sheet keyed by
        cellKey, which means the lazy useRef pattern stays correct even if
        a parent ever reuses the viewer for a different cell.
      */}
      <CrossRefPopper open={open} anchorEl={anchorRef.current} placement="bottom-start">
        <ClickAwayListener
          onClickAway={event => {
            if ((event as MouseEvent).detail === 0) return;
            setOpen(false);
          }}>
          <Box>
            {open && <CrossReferencePopupSheet cellKey={cellKey} config={config} inheritedFormat={inheritedFormat} />}
          </Box>
        </ClickAwayListener>
      </CrossRefPopper>
    </ViewerContainer>
  );
};

export default CrossReferenceValueViewer;
